<template>
  <section class="video-cont">
    <div class="video">
      <Videoplayer
        :playVideo="playVideo"
        :stopVideo="stopVideo"
        :dataVideo="dataVideo"
        ref="video"
        :dataEnter="enter"
      />

      <span class="black-cover" data-videoEls v-if="enter"></span>

      <div class="canvas-cont" data-videoEls></div>

      <div
        class="play"
        v-show="enter"
        ref="play"
      >
        <div class="play-cont" :data-state="isVideoPlaying ? 'hidden' : 'visible'">
          <div class="play-dimension">Play</div>
          <div class="play__item" data-delay="0">Play</div>
          <div class="play__item item--opacity" data-delay="1">Play</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import gsap from 'gsap';
import { mapGetters } from 'vuex';

import Videoplayer from '@/components/Videoplayer';

import PIXIRender from '@/assets/lib/video/pixirenderer';
import { isMobile } from '@/scripts/utils';

require('intersection-observer');


export default {
  name: 'Video',

  components: {
    Videoplayer,
  },

  data() {
    return {
      canvas: null,
      isVideoPlaying: false,
      dataVideo: {},
      entered: false,
      enter: false,
      isMobile,
    };
  },

  computed: {
    ...mapGetters(['getDataCityComponent']),
  },

  created() {
    this.dataVideo = this.getDataCityComponent(this.$route.params.city, 'video');

    this.needsUpdate = false;
    this.isVideoPlaying = false;
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'video-canvas';
    this.canvas.style.position = 'absolute';
    this.canvas.style.zIndex = '1';
  },

  mounted() {
    const animateInCnv = () => {
      this.PIXIRenderers.setTexture(0);

      this.injectCanvas(1, this.canvas);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (this.entered) this.PIXIRenderers.phaseOut(0.5, true);

        if (entries[0].intersectionRatio <= 0) return;

        if (this.entered) this.PIXIRenderers.phaseIn(0.1, true);

        if (!this.entered) {
          this.entered = true;

          this.$set(this.$el, 'pause', true);

          this.injectCanvas(1, this.canvas);

          this.PIXIRenderers.phaseIn(1.5, true);

          gsap.fromTo(entries[0].target, 0.5, {
            webkitClipPath: 'inset(0.01% 100% 0.03% 0.04%)',
            scale: 1.3,
          }, {
            webkitClipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
            clearProps: 'webkitClipPath',
            scale: 1,
            delay: 0.5,
            onComplete: () => {
              this.enter = true;
            },
          });
        }
      });

      this.intersectionObserver.observe(this.$el);
    };

    const img = this.switchImg();

    const loadLib = new Promise((resolve) => {
      this.PIXIRenderers = new PIXIRender(this.canvas, img, resolve);
    });
    loadLib.then(animateInCnv());
  },

  methods: {
    playVideo() {
      this.$store.commit('SET_STATEVIDEO', true);

      this.isVideoPlaying = true;

      gsap.to('.black-cover', 0.4, { autoAlpha: 0, delay: 0.3 });

      this.PIXIRenderers.phaseOut(0.5);
    },

    stopVideo() {
      this.$store.commit('SET_STATEVIDEO', false);

      this.isVideoPlaying = false;

      gsap.to('.black-cover', 0.4, { autoAlpha: 1 });

      this.PIXIRenderers.phaseIn(1.5, true);
    },

    injectCanvas(index, canvas) {
      const cont = this.$el.querySelector('.canvas-cont');
      cont.appendChild(canvas);
    },

    switchImg() {
      if ((window.chrome && window.chrome.webstore) || (window.opr && window.opr.addons)) {
        return `${this.dataVideo.url}.webp`;
      }
      return `${this.dataVideo.url}.jpg`;
    },
  },

  beforeDestroy() {
    if (this.entered) {
      this.PIXIRenderers.destroy();
      this.intersectionObserver.unobserve(this.$el);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';
@import '../application';

:global {
  @keyframes infiniteMove {
    0% {
      opacity: 1;
      transform: translateX(-100%);
    }
    100% {
      opacity: 1;
      transform: translateX(100%);
    }
  }
}

.video-cont {
  position: relative;
  padding: 100px 0 40px;

  @include mq(sm) {
    padding: 100px 0;
  }
}

[data-margin="100"] + .video-cont {
  padding: 40px 0;

  @include mq(sm) {
    padding: 100px 0;
  }
}

.video {
  position: relative;
  width: 100%;
  margin: 0;

  @include mq(sm) {
    width: calc(100% + 80px);
    margin: 0 0 0 -40px;
  }

  overflow: hidden;
  cursor: pointer;

  @at-root .pointerevents:not(.touchevents) &:hover .play,
  .no-pointerevents.no-touchevents &:hover .play {
    color: $color-blue;
  }

  .black-cover {
    position: absolute;
    display: inline-block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-color: $color-black;
    pointer-events: none;
  }

  .play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);

    font-family: 'Neutral', 'Arial', sans-serif;
    font-size: 2.75rem;
    font-weight: bold;
    letter-spacing: .125rem;
    -webkit-text-stroke: 2px #1217ff;
    color: transparent;

    pointer-events: none;
    overflow: hidden;

    transition: color 0.5s ease;

    &-cont {
      position: relative;

      &[data-state="visible"] {
        opacity: 1;
        transition: opacity 0.5s ease 1.2s;
      }

      &[data-state="hidden"] {
        opacity: 0;
        transition: opacity 0.5s ease 0;
      }
    }

    &-dimension {
      -webkit-text-stroke: 0 transparent;
      color: transparent;
      padding: 10px;
    }

    &__item {
      position: absolute;
      top: 0;
      left: 0;
      padding: 10px;

      @include boost-performance(false);
    }

    .item--opacity {
      opacity: 0;
    }

    [data-delay="0"] {
      animation: infiniteMove 2.2s infinite normal linear forwards 0s;
    }

    [data-delay="1"] {
      animation: infiniteMove 2.2s infinite normal linear forwards 1.1s;
    }
  }
}

.canvas-cont {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/Video.vue